import {
  Box,
  Button,
  FormField,
  Input,
  NonCancelableCustomEvent,
  RadioGroup,
  Select,
  SpaceBetween,
} from "@amzn/awsui-components-console";
import { BaseChangeDetail } from "node_modules/@amzn/awsui-components-console/input/interfaces";
import { OptionDefinition } from "node_modules/@amzn/awsui-components-console/internal/components/option/interfaces";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ProcessCartAction } from "@/components/cart/types";
import { useCart } from "@/contexts/CartContext";
import { useNotifications } from "@/contexts/NotificationsContext";

import useModal from "../useModal";
import { actionItems } from "./config";
import useCartCollections from "./useCartCollections";
import { validateCollectionName } from "./utils";

const useProcessCartModal = () => {
  const {
    loading: itemsLoading,
    items: collections,
    getCollections,
    createCollection,
    updateCollection,
  } = useCartCollections();

  const { items, clearCart } = useCart();

  // Initial load.
  useEffect(() => {
    getCollections();
  }, [getCollections]);

  const collectionItems = useMemo<OptionDefinition[]>(
    () =>
      collections?.map(({ id, title }) => ({
        label: title,
        value: id,
      })),
    [collections]
  );

  const [actionType, setActionType] = useState<ProcessCartAction>(
    ProcessCartAction.UPDATE
  );
  // Existing Collection.
  const [selected, setSelected] = useState<OptionDefinition | null>(null);
  // Name for a new one.
  const [touched, setTouched] = useState(false);
  const [name, setName] = useState<string>("");
  const nameInputRef = useRef<HTMLTextAreaElement>(null);
  // Processing.
  const [loading, setLoading] = useState(false);

  const { notifySuccess, notifyError } = useNotifications();

  const navigate = useNavigate();

  const { isValid: isNameValid, message: nameErrorMessage } =
    validateCollectionName(name, collections);

  const isValid =
    (actionType === ProcessCartAction.CREATE && isNameValid) ||
    (actionType === ProcessCartAction.UPDATE && selected !== null);

  const handleNameChange = ({
    detail: { value },
  }: NonCancelableCustomEvent<BaseChangeDetail>) => {
    setTouched(true);
    setName(value);
  };

  const handleProcess = async () => {
    if (isValid) {
      setLoading(true);
      const assetIds = items.map(({ assetId }) => assetId);
      try {
        const collectionId: string =
          actionType === ProcessCartAction.CREATE
            ? await createCollection(name, assetIds)
            : await updateCollection(selected!.value!, assetIds);

        clearCart();
        notifySuccess(
          <>
            Your collection has been successfully{" "}
            {actionType === ProcessCartAction.CREATE ? "created" : "updated"}.
            See it in{" "}
            <Button
              variant="inline-link"
              href={`https://app.iconik.io/collection/${collectionId}`}
              external
            >
              Iconik
            </Button>
          </>
        );

        // Clips left the cart for a collection; drop the user into a fresh
        // chat so they start a new line of work rather than lingering on the
        // now-empty cart. Only on success — a failure keeps the modal open.
        navigate("/");
      } catch (e) {
        console.error(e);
        notifyError(
          "Process Cart Error",
          "Something wrong happened. Please try again later."
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const { visible, ...rest } = useModal({
    title: "Process Cart",
    onConfirm: handleProcess,
    loading,
    disabled: !isValid,
    content: (
      <SpaceBetween size="m">
        <FormField label="Action Type">
          <RadioGroup
            onChange={({ detail: { value } }) =>
              setActionType(value as ProcessCartAction)
            }
            value={actionType}
            items={actionItems}
          />
        </FormField>
        {actionType === ProcessCartAction.UPDATE && (
          <FormField
            label="Existing Collection"
            description="This is the Iconik collection to add the clips to. These are only collections created by this application."
          >
            <Select
              selectedOption={selected}
              onChange={({ detail }) => setSelected(detail.selectedOption)}
              options={collectionItems}
              filteringType="auto"
              empty="No collections found."
              {...(itemsLoading
                ? {
                    loadingText: "Loading collections",
                    statusType: "loading",
                  }
                : {})}
              placeholder="Choose a collection"
            />
          </FormField>
        )}
        {actionType === ProcessCartAction.CREATE && (
          <FormField
            label="Collection Name"
            description="This will be the name of the collection created in Iconik."
            errorText={touched && !isNameValid ? nameErrorMessage : undefined}
          >
            <Input
              ref={nameInputRef}
              placeholder="Your Collection Name"
              value={name}
              onChange={handleNameChange}
            />
          </FormField>
        )}

        <Box>
          By continuing, you acknowledge that these clips will be added to the
          collection and cleared from the cart.
        </Box>
      </SpaceBetween>
    ),
  });

  useEffect(() => {
    // Reset.
    if (!visible) {
      setActionType(ProcessCartAction.UPDATE);
      setSelected(null);
      setName("");
      setTouched(false);
    } else if (actionType === ProcessCartAction.CREATE) {
      nameInputRef.current?.focus();
    }
  }, [visible, actionType]);

  return rest;
};

export default useProcessCartModal;
